// DocentesPorPerfilSection.js — "Líderes de opinión": los docentes de los
// módulos de cada perfil (subrol), en el mismo orden que el programa.
//
// No hay que elegirlos a mano: salen de los contenidos asignados a cada perfil y
// se ordenan con el mismo config.orden_por_perfil de la lista de módulos, sin
// repetir a quien dicta más de uno.
import React from 'react';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { useBloquesPorPerfil, docentesDeDocs } from '../perfilesDocs';

const Wrapper = styled.section`
  padding: ${p => p.$padding || '3.5rem 2rem 4rem'};
  background: ${p => p.$bg || 'transparent'};
  @media (max-width: 768px) { padding: ${p => p.$paddingMovil || '2.5rem 1.25rem 3rem'}; }
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1100px'};
  margin: 0 auto;
`;

const Titulo = styled.h2`
  margin: ${p => p.$margen || '0 0 1.6rem'};
  font-family: inherit;
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '28px'};
  color: ${p => p.$color || '#032e44'};
  text-align: ${p => p.$align || 'left'};
  text-transform: ${p => p.$transform || 'none'};
  letter-spacing: 0.2px;
`;

const Etiqueta = styled.h3`
  margin: ${p => p.$margen || '0 0 1.3rem'};
  font-family: inherit;
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '15px'};
  color: ${p => p.$color || '#032e44'};
  text-align: ${p => p.$align || 'left'};
  text-transform: ${p => p.$transform || 'uppercase'};
  letter-spacing: 0.4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 2}, 1fr);
  column-gap: ${p => p.$gap || '3.5rem'};
  row-gap: ${p => p.$gapFilas || '2rem'};
  align-items: stretch;
  @media (max-width: 900px) { grid-template-columns: 1fr; column-gap: 0; }
`;

/* La foto sobresale por la izquierda y tapa el borde de la tarjeta, como en el
   diseño: por eso la tarjeta lleva margen izquierdo y la foto margen negativo. */
const Tarjeta = styled.article`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.$gapFoto || '25px'};
  margin-left: ${p => p.$sangria || '42px'};
  background: ${p => p.$bg || 'transparent'};
  border: ${p => p.$grosor || '1px'} solid ${p => p.$borde || '#2370b6'};
  border-radius: ${p => p.$radio || '0 40px 0 0'};
  padding: ${p => p.$padding || '26px 22px 22px 0'};
  height: 100%;
  box-sizing: border-box;
`;

const Foto = styled.div`
  width: ${p => p.$size || '110px'};
  height: ${p => p.$size || '110px'};
  flex: 0 0 auto;
  margin-left: -${p => p.$sangria || '42px'};
  margin-top: -${p => p.$subida || '13px'};
  border-radius: 50%;
  border: ${p => p.$anillo || '4px'} solid ${p => p.$colorAnillo || '#ffffff'};
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center top;
  background-color: #e2e8f0;
`;

const Cuerpo = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: ${p => p.$padTop || '0'};
`;

const Nombre = styled.div`
  font-weight: ${p => p.$peso || '700'};
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#00425c'};
  line-height: 1.25;
  margin-bottom: ${p => p.$margen || '0.5rem'};
`;

/* La descripción del docente ya viene como <ul><li>; aquí se le cambia la viñeta
   por la flecha del diseño. */
const Desc = styled.div`
  font-size: ${p => p.$size || '15px'};
  color: ${p => p.$color || '#767676'};
  line-height: ${p => p.$interlineado || '1.4'};

  ul, ol { list-style: none; margin: 0; padding: 0; }
  li {
    position: relative;
    padding-left: 1.15em;
    margin-bottom: 0.15rem;
  }
  li::before {
    content: '${p => p.$vineta || '❯'}';
    position: absolute;
    left: 0;
    color: ${p => p.$colorVineta || '#2370b6'};
    font-weight: 700;
  }
  p { margin: 0 0 0.3rem; }
  p:last-child { margin-bottom: 0; }
`;

const Vacio = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: ${p => p.$color || '#94a3b8'};
`;

const resolveImg = (img) => (!img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`));

const DocentesPorPerfilSection = ({ config = {}, proyecto, tl }) => {
  const c = config;
  const traducir = (v, fallback = '') => (tl ? (tl(v) || fallback) : (v || fallback));
  const { bloques, verTodos, sinPerfil, cargando } = useBloquesPorPerfil(c, proyecto);

  if (cargando) return null;
  if (sinPerfil && c.ocultar_sin_perfil === true) return null;

  const ocultosDocentes = (Array.isArray(c.docentes_ocultos) ? c.docentes_ocultos : []).map(String);

  const grupos = bloques
    .map(b => ({
      ...b,
      docentes: docentesDeDocs(b.docs).filter(d => !ocultosDocentes.includes(String(d.id))),
    }))
    .filter(b => (verTodos || c.mostrar_grupo_vacio === true) ? true : b.docentes.length > 0);

  if (grupos.length === 0) {
    if (c.ocultar_si_vacio !== false) return null;
    return (
      <Wrapper $bg={buildBg(c, 'transparent')} $padding={c.padding} $paddingMovil={c.padding_movil}>
        <Inner $maxWidth={c.ancho_maximo}>
          <Vacio $color={c.color_texto_vacio}>{traducir(c.texto_vacio, '') || 'Todavía no hay docentes para tu perfil.'}</Vacio>
        </Inner>
      </Wrapper>
    );
  }

  const etiquetaDe = (b) => {
    const propia = traducir((c.etiquetas_grupo || {})[b.key], '');
    if (propia) return propia;
    if (c.mostrar_etiqueta_grupo === false) return '';
    return b.label || '';
  };

  return (
    <Wrapper
      $bg={buildBg(c, 'transparent')}
      $padding={c.padding}
      $paddingMovil={c.padding_movil}
    >
      <Inner $maxWidth={c.ancho_maximo}>
        {c.titulo && (
          <Titulo
            $size={c.tamano_titulo}
            $color={c.color_titulo}
            $peso={c.peso_titulo}
            $align={c.alineacion_titulo}
            $margen={c.margen_titulo}
            $transform={c.transformar_titulo}
            dangerouslySetInnerHTML={{ __html: traducir(c.titulo) }}
          />
        )}

        {grupos.map((b, i) => {
          const etiqueta = etiquetaDe(b);
          return (
            <div key={b.key || '_sin_perfil'} style={i > 0 ? { marginTop: c.separacion_grupos || '3rem' } : undefined}>
              {etiqueta && (
                <Etiqueta
                  $size={c.tamano_etiqueta}
                  $color={c.color_etiqueta}
                  $peso={c.peso_etiqueta}
                  $align={c.alineacion_titulo}
                  $margen={c.margen_etiqueta}
                  $transform={c.transformar_etiqueta}
                >
                  {etiqueta}
                </Etiqueta>
              )}
              <Grid $cols={parseInt(c.columnas, 10) || 2} $gap={c.gap} $gapFilas={c.gap_filas}>
                {b.docentes.map(d => (
                  <Tarjeta
                    key={`${b.key}_${d.id}`}
                    $bg={c.color_tarjeta}
                    $borde={c.color_borde}
                    $grosor={c.grosor_borde}
                    $radio={c.radio_tarjeta}
                    $padding={c.padding_tarjeta}
                    $sangria={c.desborde_foto}
                    $gapFoto={c.gap_foto}
                  >
                    {c.mostrar_foto !== false && (
                      <Foto
                        $src={resolveImg(d.foto)}
                        $size={c.tamano_foto}
                        $sangria={c.desborde_foto}
                        $subida={c.desborde_foto_arriba}
                        $anillo={c.grosor_anillo_foto}
                        $colorAnillo={c.color_anillo_foto}
                      />
                    )}
                    <Cuerpo $padTop={c.padding_superior_texto}>
                      <Nombre
                        $size={c.tamano_nombre}
                        $color={c.color_nombre}
                        $peso={c.peso_nombre}
                        $margen={c.margen_nombre}
                      >
                        {d.nombre}
                      </Nombre>
                      {c.mostrar_descripcion !== false && d.descripcion && (
                        <Desc
                          $size={c.tamano_descripcion}
                          $color={c.color_descripcion}
                          $interlineado={c.interlineado_descripcion}
                          $vineta={c.vineta}
                          $colorVineta={c.color_vineta}
                          dangerouslySetInnerHTML={{ __html: d.descripcion }}
                        />
                      )}
                    </Cuerpo>
                  </Tarjeta>
                ))}
              </Grid>
            </div>
          );
        })}
      </Inner>
    </Wrapper>
  );
};

export default DocentesPorPerfilSection;
